import { SelectionModel } from '@angular/cdk/collections';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Moment } from 'moment';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  defer,
  distinctUntilChanged,
  firstValueFrom,
  from,
  map, merge,
  switchMap, tap } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import {
  Batch,
  BatchDocumentStatus,
  BatchPopulatedDocument,
  BatchStatus,
  ACTIVE_BATCH_STATUSES,
} from 'src/app/models/batch';
import { BatchService } from 'src/app/services/batch.service';
import { DocumentService } from 'src/app/services/document.service';
import { MessageService } from 'src/app/services/message.service';
import {
  BatchScore,
  computeBatchScore,
} from 'src/app/helpers/batch-score.helper';
import {
  ReanalyzeConfirmationDialogComponent,
  ReanalyzeConfirmationDialogResult
} from '../../../../dialogs/reanalyze-confirmation-dialog/reanalyze-confirmation-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Paginator } from '../../../../models/paginator';

@Component({
  selector: 'app-batch',
  templateUrl: './batch.component.html',
  styleUrls: ['./batch.component.scss']
})
export class BatchComponent implements OnInit {
  from: string = "BATCHES";
  loading: boolean = false;
  batch?: Batch;
  batchDocuments: BatchPopulatedDocument[] = [];
  score?: BatchScore;

  documentStatuses: { value: string, label: string, selected: boolean }[] = [
    {
      value: BatchDocumentStatus.OK,
      label: `BATCH.DOCUMENT_${BatchDocumentStatus.OK}`,
      selected: false
    },
    {
      value: BatchDocumentStatus.ERROR,
      label: `BATCH.DOCUMENT_${BatchDocumentStatus.ERROR}`,
      selected: false
    },
    {
      value: BatchDocumentStatus.NO_TRADEMARK,
      label: `BATCH.DOCUMENT_${BatchDocumentStatus.NO_TRADEMARK}`,
      selected: false
    },
    {
      value: BatchDocumentStatus.NO_NICE_CLASSIFICATION,
      label: `BATCH.DOCUMENT_${BatchDocumentStatus.NO_NICE_CLASSIFICATION}`,
      selected: false
    },
    {
      value: BatchDocumentStatus.NO_TERRITORY,
      label: `BATCH.DOCUMENT_${BatchDocumentStatus.NO_TERRITORY}`,
      selected: false
    },
    {
      value: BatchDocumentStatus.NO_DATE,
      label: `BATCH.DOCUMENT_${BatchDocumentStatus.NO_DATE}`,
      selected: false
    }
  ];
  statuses: BatchDocumentStatus[] = [];
  selection = new SelectionModel<BatchPopulatedDocument>(true, []);

  private readonly documentsSubscription?: Subscription;
  private readonly statusesSubject = new Subject<BatchDocumentStatus[]>();
  private readonly pagerSubject = new Subject<PageEvent>();
  private readonly sortSubject = new Subject<{ active: string, direction: string }>();
  private readonly destroyRef = inject(DestroyRef);

  batches: Paginator<BatchPopulatedDocument> = {
    docs: [],
    sort: '-createdAt',
    page: 1,
    limit: 10,
    offset: 0,
    totalDocs: 0
  }

  constructor(
    private readonly _message: MessageService,
    public readonly batchService: BatchService,
    private readonly documentService: DocumentService,
    private readonly dialog: MatDialog,
    private readonly location: Location,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService
  ) {
    this.from = window.history.state.from || "BATCHES";
    const status$ = this.statusesSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(() => ({ resetPage: true })),
    );

    const page$ = this.pagerSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(() => ({ resetPage: false }))
    );

    const sort$ = this.sortSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(() => ({ resetPage: true }))
    );

    this.documentsSubscription = merge(page$, sort$, status$).pipe(
      tap<{ resetPage: boolean }>(({ resetPage }) => {
        if (resetPage) this.resetPage();
      }),
      switchMap(() => this.getBatchDocuments()),
      takeUntilDestroyed()
    ).subscribe();
  }

  ngOnInit() {
    this.onInit();
  }

  async onInit() {
    const params = await firstValueFrom(this.route.params);
    await this.retrieveBatch(params['id']);
  }

  // animate the "reanalyze" icon button to notify it's already been triggered
  get autoRefresh(): boolean {
    return !!this.batch && ACTIVE_BATCH_STATUSES.includes(this.batch.status);
  }

  async retrieveBatch(id: string) {
    try {
      this._message.emitChange('LOADING', 'START');
      this.loading = true;
      this.batch = await this.batchService.retrieve(id);
      this.computeScore();
      this.getBatchDocuments()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.loading = false;
          this._message.emitChange('LOADING', 'END');
        });
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 404) {
        this.router.navigate(['errors', '404'])
      }
      this.loading = false;
      this._message.emitChange('LOADING', 'END');
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  getBatchDocuments() {
    if (!this.batch?._id) return EMPTY;
    const batchId = this.batch._id;

    return defer(() => {
      this._message.emitChange('LOADING', 'START');
      this.loading = true;

      return from(this.batchService.retrieveDocuments(
        batchId,
        this.batches.page,
        this.batches.limit,
        this.batches.offset,
        this.batches.sort,
        this.statuses,
      ));
    }).pipe(
      tap((result) => {
        this.batches = { ...this.batches, ...result };
        this.loading = false;
        this._message.emitChange('LOADING', 'END');
      }),
      catchError((err) => {
        this.loading = false;
        this._message.emitChange('LOADING', 'END');
        this.toastr.error('ERRORS.GENERIC');
        return EMPTY;
      }),
      map(() => void 0)
    );
  }

  async refresh() {
    if (this.batch && this.batch._id) {
      await this.retrieveBatch(this.batch._id);
    }
  }

  async reanalyze() {
    this.dialog.open(ReanalyzeConfirmationDialogComponent, {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        titleKey: 'BATCH.REANALYZE_TITLE',
        text: this.translate.instant('BATCH.REANALYZE_TEXT', this.batch),
      }
    }).afterClosed()
      .subscribe(async (result: ReanalyzeConfirmationDialogResult) => {
        if (result.confirmed && this.batch && this.batch._id){
          try {
            this._message.emitChange("LOADING", "START");
            await this.batchService.analyze(this.batch._id, result.analysis);
            this._message.emitChange("LOADING", "END");
            this.toastr.success(this.translate.instant('BATCH.REANALYZE_SUCCESS', this.batch));
            this.batch = {
              ...this.batch,
              status: BatchStatus.PENDING,
            }; // optimistic update: new object triggers the batch-progress polling
          } catch (err) {
            this.loading = false;
            this._message.emitChange("LOADING", "END");
            this.toastr.error('ERRORS.GENERIC');
          }
        }
      })
  }

  async deleteDocuments() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'BATCH.DELETE_DOCUMENTS_TITLE',
        text: this.translate.instant(this.selection.selected.length === this.batch?.documents ? 'BATCH.DELETE_DOCUMENTS_AND_BATCH_TEXT' : 'BATCH.DELETE_DOCUMENTS_TEXT', { length: this.selection.selected.length }),
        button: {
          text: 'ACTIONS.DELETE',
          class: 'danger-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed && this.batch && this.batch._id) {
      try {
        this.loading = true;
        this._message.emitChange("LOADING", "START");
        await this.documentService.deleteMany(this.selection.selected.map((doc) => doc._id));
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant(this.selection.selected.length === this.batch?.documents ? 'BATCH.BATCH_AND_DOCUMENTS_DELETED' : 'BATCH.DOCUMENTS_DELETED', { length: this.selection.selected.length }));

        if (this.selection.selected.length === this.batch?.documents) {
          this.location.back();
        } else {
          this.getBatchDocuments()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }
        this.selection.clear();

      } catch (err) {
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  onStatusChange(event: any) {
    if (event && event.value) {
      this.statuses = event.value;
      this.statusesSubject.next(this.statuses);
    }
  }


  computeScore() {
    this.score = computeBatchScore(this.batch);
  }

  formatDate(date: Moment) {
    return moment(date).format('ll');
  }

  toTrademark(trademark: any) {
    this.router.navigate(['trademarks', trademark.ref], {
      state: {
        from: "BATCH"
      }
    })
  }

  resetPage(keepLimit = true) {
    if (!keepLimit) this.batches.limit = 10;
    this.batches.page = 1;
    this.batches.offset = 0;
  }

  async handlePage(event: any) {
    if (this.batch?._id) {
      this.batches.limit = event.pageSize;
      this.batches.page = event.pageIndex + 1;
      this.batches.offset = event.pageIndex * this.batches.limit;
      this.pagerSubject.next(event);
    }
  }

  async handleSort(event: any) {
    if (this.batch?._id) {
      if (event.direction === '') {
        this.batches.sort = '-createdAt';
      } else {
        this.batches.sort = `${event.direction === 'desc' ? '-' : ''}${event.active}`;
      }
      this.sortSubject.next(event);
    }
  }

  protected async deleteBatch() {
    if (!this.batch?._id) return;

    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'BATCH.DELETE_BATCH_TITLE',
        text: this.translate.instant(
          'BATCH.DELETE_BATCH_TEXT',
          { number: this.batch.number },
        ),
        confirmation: {
          text: 'BATCH.DELETE_BATCH_CONFIRMATION',
          value: this.batch.number.toString()
        },
        button: {
          text: 'ACTIONS.DELETE',
          class: 'danger-button',
        },
      },
    };
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: {
      confirmed: boolean
    } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed && this.batch?._id) {
      try {
        this.loading = true;
        this._message.emitChange('LOADING', 'START');
        await this.batchService.delete(this.batch._id);
        this.toastr.success(
          this.translate.instant(
            'BATCH.BATCH_DELETED',
            { number: this.batch.number },
          ),
        );
        this.router.navigate(['documents', 'batches']);
      } catch {
        this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
      } finally {
        this.loading = false;
        this._message.emitChange('LOADING', 'END');
      }
    }
  }
}
